import { useMemo } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { SelectInput } from "../../../components/SelectInput";
import { FormGroup } from "../../../components/FormGroup";
import { FormLabel } from "../../../components/FormLabel";
import { InfoBox } from "../../../components/InfoBox";
import { Toggle } from "../../../components/Toggle";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import type { RestStore } from "@KassaroAB/shared/types/store";

const BASE_COLUMN_MAPPING_VALUES = [
  "",
  "NAME",
  "ARTICLE_NUMBER",
  "BARCODE",
  "MANUFACTURER_ARTICLE_NUMBER",
  "MANUFACTURER_BARCODE",
  "INFORMATION",
  "UNIT_PRICE",
  "VAT",
  "GROUP_NAME",
  "SUPPLIER",
  "UNIT",
  "BOOKKEEPING_ACCOUNT_NUMBER",
  "STOCK_COUNT",
  "STOCK_POLICY",
  "STOCK_MIN",
  "STOCK_MAX",
  "PURCHASE_PRICE",
  "TAKEAWAY_VAT",
  "TAKEAWAY_PRICE",
  "TAKEAWAY_BOOKKEEPING_ACCOUNT_NUMBER",
  "PRODUCT_SYNC_ENABLED",
  "ID",
  "GROUP_ID",
  "SUPPLIER_ID",
  "UNIT_ID",
  "BOOKKEEPING_ACCOUNT_ID",
  "TAKEAWAY_BOOKKEEPING_ACCOUNT_ID",
] as const;

const VARIANT_ID_MAPPING_VALUE = "VARIANT_ID" as const;

const RESERVED_MAPPING_VALUES = new Set<string>([
  ...BASE_COLUMN_MAPPING_VALUES.filter((value) => value !== ""),
  VARIANT_ID_MAPPING_VALUE,
  "SKIP",
]);

const ColumnMappingsContainer = styled.div`
  margin-top: 0;
`;

const MappingList = styled.div`
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.regular}px;
  overflow: hidden;
  background-color: ${colors.backgroundLight};
`;

const MappingHeaderRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.x2}px;
  align-items: center;
  padding: ${spacing.x2}px ${spacing.x3}px;
  background-color: ${colors.backgroundDark};
  border-bottom: 1px solid ${colors.border};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${colors.textMuted};
`;

const ColumnMappingRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.x2}px;
  align-items: center;
  padding: ${spacing.x2}px ${spacing.x3}px;
  border-bottom: 1px solid ${colors.border};
  background-color: ${colors.backgroundLight};

  &:last-child {
    border-bottom: none;
  }
`;

const ColumnName = styled.div`
  font-weight: 500;
  color: ${colors.text};
  font-size: 14px;
`;

const SummaryChevron = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${colors.textMuted};
  transition: transform 0.2s ease;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const VariantOptionsContainer = styled.details`
  margin-top: ${spacing.x3}px;
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.regular}px;
  background-color: ${colors.backgroundLight};
  overflow: hidden;

  &[open] ${SummaryChevron} {
    transform: rotate(180deg);
  }
`;

const VariantOptionsSummary = styled.summary`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.x2}px;
  cursor: pointer;
  padding: ${spacing.x2}px ${spacing.x3}px;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text};
  list-style: none;

  &::-webkit-details-marker {
    display: none;
  }

  &:hover {
    background-color: ${colors.backgroundDark};
  }
`;

const VariantOptionsBody = styled.div`
  padding: 0 ${spacing.x3}px ${spacing.x3}px;
  border-top: 1px solid ${colors.border};
  display: flex;
  flex-direction: column;
  gap: ${spacing.x3}px;
`;

const VariantHelpText = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${colors.textMuted};
`;

type ExtraProductFieldRow = NonNullable<NonNullable<RestStore["extraProductFields"]>[number]>;

interface ColumnMappingProps {
  headers: string[];
  mappings: Record<number, string>;
  onMappingChange: (index: number, value: string) => void;
  extraProductFields?: ExtraProductFieldRow[] | null;
  showVariants?: boolean;
  variantGroupColumn?: string;
  onVariantGroupColumnChange?: (value: string) => void;
  firstRowAsVariant?: boolean;
  onFirstRowAsVariantChange?: (value: boolean) => void;
}

const EXTRA_FIELD_MAPPING_PREFIX = "extra:";

const toExtraFieldMappingValue = (extraFieldKey: string): string =>
  `${EXTRA_FIELD_MAPPING_PREFIX}${extraFieldKey}`;

const parseExtraFieldMappingValue = (field: string): string | null => {
  if (!field.startsWith(EXTRA_FIELD_MAPPING_PREFIX)) {
    return null;
  }
  const extraFieldKey = field.slice(EXTRA_FIELD_MAPPING_PREFIX.length);
  if (extraFieldKey.length === 0) {
    return null;
  }
  return extraFieldKey;
};

const getMappedFieldLabel = (
  field: string,
  extraProductFields: ExtraProductFieldRow[] | null | undefined,
  translate: (key: string, options?: Record<string, string>) => string,
): string => {
  const prefixedExtraFieldKey = parseExtraFieldMappingValue(field);
  if (prefixedExtraFieldKey !== null) {
    const extraField = (extraProductFields ?? []).find(
      (entry) => entry.key === prefixedExtraFieldKey,
    );
    return translate("productImport.extraFieldLabel", {
      name: extraField?.name || prefixedExtraFieldKey,
    });
  }
  const extraField = (extraProductFields ?? []).find(
    (entry) => entry.key === field,
  );
  if (extraField && !RESERVED_MAPPING_VALUES.has(field)) {
    return translate("productImport.extraFieldLabel", {
      name: extraField.name || extraField.key,
    });
  }
  return translate(`productImport.columnField.${field}`);
};

export const ColumnMapping = ({
  headers,
  mappings,
  onMappingChange,
  extraProductFields,
  showVariants = false,
  variantGroupColumn = "",
  onVariantGroupColumnChange,
  firstRowAsVariant = false,
  onFirstRowAsVariantChange,
}: ColumnMappingProps) => {
  const { t } = useTranslation();

  const selectOptions = useMemo(() => {
    const columnValues = showVariants
      ? [...BASE_COLUMN_MAPPING_VALUES, VARIANT_ID_MAPPING_VALUE]
      : BASE_COLUMN_MAPPING_VALUES;
    const base = columnValues.map((value) => ({
      value,
      label: t(`productImport.columnField.${value === "" ? "SKIP" : value}`),
    }));
    const extra = (extraProductFields ?? []).map((field) => ({
      value: toExtraFieldMappingValue(field.key),
      label: t("productImport.extraFieldLabel", {
        name: field.name || field.key,
      }),
    }));
    return [...base, ...extra];
  }, [extraProductFields, showVariants, t]);

  const mappedFields = useMemo(() => {
    const uniqueFields = new Set<string>();
    Object.values(mappings).forEach((field) => {
      if (field && field !== "SKIP" && field !== VARIANT_ID_MAPPING_VALUE) {
        uniqueFields.add(field);
      }
    });
    return Array.from(uniqueFields);
  }, [mappings]);

  const variantGroupColumnOptions = useMemo(() => {
    const fieldToSourceColumn = new Map<string, string>();
    headers.forEach((header, index) => {
      const field = mappings[index];
      if (field && field !== "SKIP" && field !== VARIANT_ID_MAPPING_VALUE) {
        if (!fieldToSourceColumn.has(field)) {
          fieldToSourceColumn.set(field, header);
        }
      }
    });

    return mappedFields.map((field) => {
      const sourceColumn = fieldToSourceColumn.get(field) ?? field;
      const targetFieldLabel = getMappedFieldLabel(field, extraProductFields, t);
      return {
        field,
        label: t("productImport.variantGroupColumnOption", {
          sourceColumn,
          targetField: targetFieldLabel,
        }),
      };
    });
  }, [extraProductFields, headers, mappedFields, mappings, t]);

  const hasVariantIdMapping = Object.values(mappings).includes(VARIANT_ID_MAPPING_VALUE);
  const variantImportEnabled = mappedFields.length > 0;

  if (headers.length === 0) {
    return null;
  }

  return (
    <ColumnMappingsContainer>
      <MappingList>
        <MappingHeaderRow>
          <span>{t("productImport.mappingTableSourceColumn")}</span>
          <span>{t("productImport.mappingTableTargetField")}</span>
        </MappingHeaderRow>
        {headers.map((header, index) => (
          <ColumnMappingRow key={index}>
            <ColumnName>{header}</ColumnName>
            <SelectInput
              value={mappings[index] || ""}
              onChange={(e) => onMappingChange(index, e.target.value)}
            >
              {selectOptions.map((opt) => (
                <option key={opt.value || "SKIP"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </SelectInput>
          </ColumnMappingRow>
        ))}
      </MappingList>

      {showVariants && (
        <VariantOptionsContainer>
          <VariantOptionsSummary>
            {t("productImport.variantsSectionTitle")}
            <SummaryChevron aria-hidden>
              <ChevronDown strokeWidth={2} />
            </SummaryChevron>
          </VariantOptionsSummary>
          <VariantOptionsBody>
            <InfoBox variant="info">
              {firstRowAsVariant
                ? t("productImport.variantsHelpRetail")
                : t("productImport.variantsHelpRestaurant")}
            </InfoBox>
            {hasVariantIdMapping && (
              <VariantHelpText>{t("productImport.variantsVariantIdOptionalHint")}</VariantHelpText>
            )}
            <FormGroup>
              <FormLabel>{t("productImport.variantGroupColumnLabel")}</FormLabel>
              <SelectInput
                value={variantGroupColumn}
                onChange={(event) => onVariantGroupColumnChange?.(event.target.value)}
                disabled={!variantImportEnabled}
              >
                <option value="">{t("productImport.variantGroupColumnPlaceholder")}</option>
                {variantGroupColumnOptions.map((option) => (
                  <option key={option.field} value={option.field}>
                    {option.label}
                  </option>
                ))}
              </SelectInput>
              <VariantHelpText>{t("productImport.variantGroupColumnHelp")}</VariantHelpText>
            </FormGroup>
            <FormGroup>
              <FormLabel>{t("productImport.firstRowAsVariantLabel")}</FormLabel>
              <Toggle
                checked={firstRowAsVariant}
                onChange={(value) => onFirstRowAsVariantChange?.(value)}
                label={t("productImport.firstRowAsVariantToggle")}
                disabled={!variantImportEnabled || !variantGroupColumn}
              />
              <VariantHelpText>
                {firstRowAsVariant
                  ? t("productImport.firstRowAsVariantHelpRetail")
                  : t("productImport.firstRowAsVariantHelpRestaurant")}
              </VariantHelpText>
            </FormGroup>
          </VariantOptionsBody>
        </VariantOptionsContainer>
      )}
    </ColumnMappingsContainer>
  );
};
